import type { ReactNode } from 'react';
import { AlertCircle, CheckCircle2, Circle, Globe } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/app/components/ui/card';
import { TabsContent } from '@/app/components/ui/tabs';
import type { EditorReady } from './types';

type Props = {
  ed: EditorReady;
  adBar: ReactNode;
  rowPicker: ReactNode;
  previewHeadlines: string[];
  previewDescriptions: string[];
  previewDisplayUrl: string;
  filledHeadlines: number;
  filledDescriptions: number;
  keywordCount: number;
  hasUrl: boolean;
  hasCampaignName: boolean;
  hasAdGroupName: boolean;
};

export function SummaryTab({
  ed, adBar, rowPicker,
  previewHeadlines, previewDescriptions, previewDisplayUrl,
  filledHeadlines, filledDescriptions, keywordCount,
  hasUrl, hasCampaignName, hasAdGroupName,
}: Props) {
  const {
    templateSet, cities, previewRow, splitStructureStats,
    activeHeadlines, activeDescriptions, isMultiAd, activeAdIndex,
    renderWithRow,
  } = ed;

  return (
    <TabsContent value="summary" className="space-y-5">
      {/* Row Picker */}
      {cities.length > 0 && (
        <div className="flex items-center gap-3">
          {rowPicker}
          <span className="text-xs text-muted-foreground/50">
            previewing 1 of {cities.length.toLocaleString()} rows
          </span>
        </div>
      )}

      {/* Google Ads Preview */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center gap-2">
            <Globe className="size-3.5 text-muted-foreground" />
            <CardTitle className="text-sm font-sans font-medium text-muted-foreground">Google Ads Preview</CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          <div className="bg-white dark:bg-[#1c1c2e] border border-[#dadce0] dark:border-[#2a2a4a] rounded-xl p-4 max-w-2xl shadow-sm">
            <div className="flex items-center gap-2 mb-1.5">
              <span className="text-[11px] border border-[#006621] dark:border-[#4caf50] text-[#006621] dark:text-[#4caf50] rounded px-1 leading-tight font-medium">Ad</span>
              <span className="text-xs text-[#202124] dark:text-[#9aa0a6]">
                {previewDisplayUrl || <span className="italic text-muted-foreground/40">No URL configured</span>}
              </span>
            </div>
            <div className="text-[#1a0dab] dark:text-[#8ab4f8] text-[18px] font-normal leading-tight mb-1.5 font-sans">
              {previewHeadlines.length > 0
                ? previewHeadlines.slice(0, 3).join(' | ')
                : <span className="text-muted-foreground/40 italic text-base">No headlines configured</span>}
            </div>
            {previewDescriptions.length > 0
              ? previewDescriptions.slice(0, 2).map((d, i) => (
                <p key={i} className="text-sm text-[#4d5156] dark:text-[#bdc1c6] leading-snug">{d}</p>
              ))
              : <p className="text-sm text-muted-foreground/40 italic">No descriptions configured</p>}
          </div>

          {previewHeadlines.length > 3 && (
            <div className="mt-4 space-y-2">
              <p className="text-[10px] font-mono text-muted-foreground/60 uppercase tracking-widest">All {previewHeadlines.length} headlines</p>
              <div className="flex flex-wrap gap-1.5">
                {previewHeadlines.map((h, i) => (
                  <span key={i} className="text-xs bg-primary/8 border border-primary/20 text-primary/80 px-2 py-0.5 rounded font-sans">
                    <span className="font-mono text-[10px] text-muted-foreground mr-1">H{i + 1}</span>{h}
                  </span>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Campaign Structure */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm">Campaign Structure</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            {[
              { label: 'Campaign', template: templateSet.template_content.campaign_name_template },
              { label: 'Ad Group', template: templateSet.template_content.ad_group_name_template },
            ].map(({ label, template }) => (
              <div key={label} className="p-3 bg-muted/30 rounded-lg space-y-1">
                <p className="text-[10px] font-mono text-muted-foreground/60 uppercase tracking-widest">{label}</p>
                <p className="text-sm font-medium">
                  {template ? renderWithRow(template, previewRow) : <span className="text-muted-foreground/40 italic text-xs">Not configured</span>}
                </p>
                {template && <p className="text-[10px] text-muted-foreground/50 font-mono truncate">{template}</p>}
              </div>
            ))}
          </div>
          {splitStructureStats && (
            <div className="flex items-center gap-2 text-sm bg-blue-50 dark:bg-blue-950/20 rounded-md p-2.5">
              <span className="font-mono text-muted-foreground text-xs">{splitStructureStats.primaryCount} {splitStructureStats.primaryLabel}</span>
              <span className="text-muted-foreground/40">×</span>
              <span className="font-mono text-muted-foreground text-xs">{splitStructureStats.splitCount} {splitStructureStats.splitLabel}</span>
              <span className="text-muted-foreground/40">=</span>
              <span className="font-mono font-semibold text-primary text-xs">{splitStructureStats.campaignCount} campaigns</span>
            </div>
          )}
        </CardContent>
      </Card>

      {/* All Headlines & Descriptions */}
      {isMultiAd && adBar}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">All Headlines <span className="font-mono font-normal text-muted-foreground/50 text-xs ml-1">({filledHeadlines}/15 filled{isMultiAd ? ` · Ad ${activeAdIndex}` : ''})</span></CardTitle>
        </CardHeader>
        <CardContent>
          {filledHeadlines === 0 ? (
            <p className="text-sm text-muted-foreground/50 italic">No headlines configured</p>
          ) : (
            <div className="space-y-1">
              {activeHeadlines.map((slot) => {
                const v = slot.variants.find(v => v.enabled && v.template_string.trim());
                if (!v) return (
                  <div key={slot.slot_number} className="grid gap-3 py-1 items-center opacity-30"
                    style={{ gridTemplateColumns: '32px 1fr 60px' }}>
                    <span className="text-[10px] font-mono text-muted-foreground">H{slot.slot_number}</span>
                    <span className="text-xs text-muted-foreground/40 italic">—</span>
                    <span />
                  </div>
                );
                const rendered = renderWithRow(v.template_string, previewRow);
                const over = rendered.length > 30;
                return (
                  <div key={slot.slot_number} className="grid gap-3 py-1.5 items-center border-b border-border/20 last:border-0"
                    style={{ gridTemplateColumns: '32px 1fr 60px' }}>
                    <span className="text-[10px] font-mono text-muted-foreground/60">H{slot.slot_number}</span>
                    <span className={`text-sm ${over ? 'text-destructive' : 'text-foreground'}`}>{rendered}</span>
                    <span className={`text-[10px] font-mono text-right ${over ? 'text-destructive font-semibold' : 'text-muted-foreground/50'}`}>{rendered.length}/30</span>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">All Descriptions <span className="font-mono font-normal text-muted-foreground/50 text-xs ml-1">({filledDescriptions}/4 filled{isMultiAd ? ` · Ad ${activeAdIndex}` : ''})</span></CardTitle>
        </CardHeader>
        <CardContent>
          {filledDescriptions === 0 ? (
            <p className="text-sm text-muted-foreground/50 italic">No descriptions configured</p>
          ) : (
            <div className="space-y-1">
              {activeDescriptions.map((slot) => {
                const v = slot.variants.find(v => v.enabled && v.template_string.trim());
                if (!v) return (
                  <div key={slot.slot_number} className="grid gap-3 py-1 items-center opacity-30"
                    style={{ gridTemplateColumns: '32px 1fr 60px' }}>
                    <span className="text-[10px] font-mono text-muted-foreground">D{slot.slot_number}</span>
                    <span className="text-xs text-muted-foreground/40 italic">—</span>
                    <span />
                  </div>
                );
                const rendered = renderWithRow(v.template_string, previewRow);
                const over = rendered.length > 90;
                return (
                  <div key={slot.slot_number} className="grid gap-3 py-1.5 items-start border-b border-border/20 last:border-0"
                    style={{ gridTemplateColumns: '32px 1fr 60px' }}>
                    <span className="text-[10px] font-mono text-muted-foreground/60 pt-0.5">D{slot.slot_number}</span>
                    <span className={`text-sm leading-relaxed ${over ? 'text-destructive' : 'text-foreground'}`}>{rendered}</span>
                    <span className={`text-[10px] font-mono text-right pt-0.5 ${over ? 'text-destructive font-semibold' : 'text-muted-foreground/50'}`}>{rendered.length}/90</span>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* All Keywords */}
      {keywordCount > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Keywords <span className="font-mono font-normal text-muted-foreground/50 text-xs ml-1">({keywordCount})</span></CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-1">
              {templateSet.template_content.keywords.map((kw) => {
                const v = kw.variants.find(v => v.enabled && v.template_string.trim());
                const rendered = v ? renderWithRow(v.template_string, previewRow) : '';
                return (
                  <div key={kw.id} className="grid gap-3 py-1.5 items-center border-b border-border/20 last:border-0"
                    style={{ gridTemplateColumns: '100px 1fr 80px' }}>
                    <span className="text-[10px] font-mono text-muted-foreground/60 truncate">{kw.item_name || '—'}</span>
                    <span className="text-sm text-foreground">{rendered || <span className="text-muted-foreground/40 italic">—</span>}</span>
                    <div className="flex gap-1 justify-end">
                      {kw.match_types.map(mt => (
                        <span key={mt} className="text-[9px] font-mono px-1 py-0.5 rounded bg-primary/10 border border-primary/20 text-primary/70">{mt[0].toUpperCase()}</span>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* URLs & Paths */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">URLs &amp; Paths</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="space-y-1">
            <p className="text-[10px] font-mono text-muted-foreground/50 uppercase tracking-widest">Final URL</p>
            <p className="text-sm font-mono text-blue-600 dark:text-blue-400 break-all">
              {templateSet.template_content.final_url_template
                ? renderWithRow(templateSet.template_content.final_url_template, previewRow)
                : <span className="text-muted-foreground/40 italic">Not configured</span>}
            </p>
          </div>
          <div className="grid grid-cols-2 gap-4">
            {[
              { label: 'Path 1', template: templateSet.template_content.path1_template, limit: 15 },
              { label: 'Path 2', template: templateSet.template_content.path2_template, limit: 15 },
            ].map(({ label, template, limit }) => {
              const rendered = template ? renderWithRow(template, previewRow) : '';
              const over = rendered.length > limit;
              return (
                <div key={label} className="space-y-1">
                  <p className="text-[10px] font-mono text-muted-foreground/50 uppercase tracking-widest">{label}</p>
                  <p className={`text-sm font-mono ${over ? 'text-destructive' : rendered ? 'text-foreground' : 'text-muted-foreground/40 italic'}`}>
                    {rendered || 'Not configured'}
                  </p>
                  {rendered && <p className={`text-[10px] font-mono ${over ? 'text-destructive' : 'text-muted-foreground/40'}`}>{rendered.length}/{limit}</p>}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Completeness */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm">Completeness</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-x-6 gap-y-2">
            {[
              { label: 'Campaign name', ok: hasCampaignName, warn: false },
              { label: 'Ad group name', ok: hasAdGroupName, warn: false },
              { label: `Headlines (${filledHeadlines}/15)`, ok: filledHeadlines >= 3, warn: filledHeadlines > 0 && filledHeadlines < 3 },
              { label: `Descriptions (${filledDescriptions}/4)`, ok: filledDescriptions >= 2, warn: filledDescriptions > 0 && filledDescriptions < 2 },
              { label: `Keywords (${keywordCount})`, ok: keywordCount > 0, warn: false },
              { label: 'Final URL', ok: hasUrl, warn: false },
            ].map(({ label, ok, warn }) => (
              <div key={label} className="flex items-center gap-2 text-sm">
                {ok
                  ? <CheckCircle2 className="size-3.5 text-green-500 shrink-0" />
                  : warn
                    ? <AlertCircle className="size-3.5 text-yellow-500 shrink-0" />
                    : <Circle className="size-3.5 text-muted-foreground/30 shrink-0" />}
                <span className={ok ? 'text-foreground' : 'text-muted-foreground/60'}>{label}</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </TabsContent>
  );
}
